import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Button,
  Stack,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import EventDetails from "./EventDetails";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import InstagramIcon from "@mui/icons-material/Instagram";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

/* ---------- EVENT DATA ----------
   Auto-generated from events.config.js + the photos in public/events/<folder>/.
   To add or change an event, edit events.config.js (in the project root) and
   run `npm run events` — do NOT edit event data here. */
import BUILT_IN_EVENTS from "../../../data/events.generated.json";
import { fetchSiteContent, sectionOr } from "../../../utils/siteContent";

const Events = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Events the owner manages in the admin app, falling back to the generated
  // list that ships with the build. Editing one there no longer needs a deploy.
  const [EVENTS, setEvents] = useState(BUILT_IN_EVENTS);

  useEffect(() => {
    let cancelled = false;
    fetchSiteContent().then((content) => {
      if (cancelled) return;
      const managed = sectionOr(content, "events", null);
      if (!managed) return;

      const usable = managed
        .filter((e) => !e.hidden && e.title)
        .map((e) => ({
          ...e,
          cover: e.cover || null,
          gallery: Array.isArray(e.gallery) && e.gallery.length > 0
            ? e.gallery
            : e.cover
            ? [e.cover]
            : [],
        }));

      if (usable.length > 0) setEvents(usable);
    });
    return () => { cancelled = true; };
  }, []);

  const featuredEvents = EVENTS.filter((e) => e.featured);
  const upcomingEvents = EVENTS.filter((e) => e.upcoming);
  const pastEvents = EVENTS.filter((e) => !e.upcoming);

  // ---------- EVENT DETAILS ----------
  if (selectedEvent) {
    return (
      <EventDetails
        event={selectedEvent}
        onBack={() => setSelectedEvent(null)}
      />
    );
  }

  return (
  <>
  
<Helmet>
  <title>
    Host an Event at Den Den Claw | Birthdays & Private Events in Ottawa
  </title>

  <meta
    name="description"
    content="Looking to host a birthday, private party, or community event in Ottawa? Den Den Claw offers claw machines, prizes, and a fun arcade space in Chinatown."
  />

  <link rel="canonical" href="https://www.dendenclaw.com/explore/events" />

  <script type="application/ld+json">{JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Private & Community Events at Den Den Claw",
    "description": "Host birthdays, team buildings, pop-ups, and themed arcade nights at Den Den Claw in Ottawa's Chinatown.",
    "location": {
      "@type": "Place",
      "name": "Den Den Claw",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "691 Somerset St W",
        "addressLocality": "Ottawa",
        "addressRegion": "ON",
        "postalCode": "K1R 6P5",
        "addressCountry": "CA"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": "Den Den Claw",
      "url": "https://www.dendenclaw.com"
    },
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "eventStatus": "https://schema.org/EventScheduled",
    "url": "https://www.dendenclaw.com/explore/events"
  })}</script>

</Helmet>

    <Box sx={{mb:2, p:2}}>
      {/* ---------- HEADER ---------- */}
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, textAlign: "center", mt: 2, mb: 2 }}
      >
        Events 🎉
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{ textAlign: "center", mb: 6, color: "text.secondary" }}
      >
        Den Den Claw hosts community events, private bookings, birthdays, and
        themed arcade nights in Ottawa. Take a look at some of our memories!
      </Typography>

      {/* ---------- BOOKING / CONTACT BLURB ---------- */}
<Box
  sx={{
    mb: 8,
    p: 4,
    borderRadius: 3,
    backgroundColor: "background.paper",
    boxShadow: 6,
    textAlign: "center",
  }}
>
  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
    Want to host an event at Den Den Claw? 🎉
  </Typography>

  <Typography sx={{ mb: 2, color: "text.secondary" }}>
  If you have an idea, we’d love to hear about it!
  </Typography>

  {/* ---- CONTACT OPTIONS ---- */}
<Stack
  direction={{ xs: "column", sm: "row" }}
  spacing={2}
  justifyContent="center"
>
  {/* EMAIL */}
  <Button
    variant="contained"
    startIcon={<EmailIcon />}
    href={
      "mailto:dendendori@gmail.com" +
      "?subject=Event%20Booking%20Inquiry%20-%20Den%20Den%20Claw" +
      "&body=Hi%20Den%20Den%20Claw!%0A%0AI’m%20interested%20in%20booking%20an%20event.%0A%0AEvent%20type:%0APreferred%20date:%0AEstimated%20group%20size:%0A%0AThanks!"
    }
    sx={{
      borderRadius: 999,
      px: 3,
      textTransform: "none",
      fontWeight: 600,
    }}
  >
    dendendori@gmail.com
  </Button>

  {/* TEXT */}
  <Button
    variant="outlined"
    startIcon={<PhoneIphoneIcon />}
    href="tel:16135551234"
    sx={{
      borderRadius: 999,
      px: 3,
      textTransform: "none",
      fontWeight: 600,
    }}
  >
    Text (613) 400-2909
  </Button>

  {/* INSTAGRAM */}
  <Button
    variant="outlined"
    startIcon={<InstagramIcon />}
    href="https://instagram.com/dendenclaw"
    target="_blank"
    sx={{
      borderRadius: 999,
      px: 3,
      textTransform: "none",
      fontWeight: 600,
    }}
  >
   DM @dendenclaw
  </Button>
</Stack>

</Box>


      {/* ---------- FEATURED EVENTS ---------- */}
      {featuredEvents.length > 0 && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Featured Events ✨
          </Typography>

          <Grid container spacing={4} sx={{ mb: 8 }}>
            {featuredEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => setSelectedEvent(event)}
                featured
              />
            ))}
          </Grid>
        </>
      )}

      {/* ---------- UPCOMING EVENTS ---------- */}
      {upcomingEvents.length > 0 && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Upcoming Events ⏰
          </Typography>

          <Grid container spacing={4} sx={{ mb: 8 }}>
            {upcomingEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => {}}
                upcoming
              />
            ))}
          </Grid>
        </>
      )}

      {/* ---------- POP-UP CROSS-LINK ---------- */}
      <Box
        sx={{
          mb: 8,
          p: 3,
          borderRadius: 3,
          boxShadow: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Are you a food or drink vendor? 🍡
        </Typography>
        <Typography sx={{ color: "text.secondary", maxWidth: 520, mx: "auto", mb: 2 }}>
          Pop-ups have their own calendar now — check who's coming up, or book the
          space for your own day.
        </Typography>
        <Button
          component={Link}
          to="/explore/pop-ups"
          variant="contained"
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 600 }}
        >
          See pop-ups &amp; book a date
        </Button>
      </Box>

      {/* ---------- PAST EVENTS ---------- */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Past Events 📸
      </Typography>

      <Grid container spacing={4}>
        {pastEvents.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            onClick={() => setSelectedEvent(event)}
          />
        ))}
      </Grid>
    </Box>
  </>
  );
};

/* ---------- EVENT CARD ---------- */
const EventCard = ({ event, onClick, featured, upcoming }) => {
  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={3}
      sx={{ display: "flex", justifyContent: "center" }}
    >
      <Card
        sx={{
          width: 300,
          borderRadius: 3,
          cursor: upcoming ? "default" : "pointer",
          transition: "all 0.35s ease",
          boxShadow: 8,
          "&:hover": {
            boxShadow: upcoming ? 8 : 10,
            transform: upcoming ? "none" : "translateY(-4px)",
          },
          "&:hover img": {
            transform: upcoming ? "none" : "scale(1.05)",
          },
          "&:hover .event-title": {
            color: upcoming ? "inherit" : "#0f7ca4",
          },
        }}
      >
        {(featured || upcoming) && (
          <Chip
            label={featured ? "Featured" : "Upcoming"}
            color={featured ? "primary" : "secondary"}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 2,
              fontWeight: 600,
            }}
          />
        )}

        <CardActionArea
          onClick={!upcoming ? onClick : undefined}
          disableRipple
          disabled={upcoming}
          sx={{
            "& .MuiCardActionArea-focusHighlight": {
              opacity: 0,
            },
          }}
        >
          <CardMedia
            component="img"
            height="200"
            image={event.cover}
            alt={event.title}
            sx={{
              transition: "transform 0.35s ease",
            }}
          />

          <CardContent
            sx={{
              textAlign: "center",
              py: 2.5,
            }}
          >
            <Typography
              className="event-title"
              variant="h6"
              sx={{ fontWeight: 600 }}
            >
              {event.title}
            </Typography>

            {event.date && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.5 }}
              >
                {event.date}
              </Typography>
            )}

            {!upcoming && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  opacity: 0.8,
                  transition: "opacity 0.3s ease",
                  ".MuiCard-root:hover &": {
                    opacity: 1,
                  },
                }}
              >
                View event →
              </Typography>
            )}

            {upcoming && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                Details coming soon
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

export default Events;
